import ExcelJS from 'exceljs'

export const SHEET_COMPANIES = 'Companies'
export const SHEET_ALLOCATIONS = 'Allocations'
export const SHEET_GUESTS = 'Guests'
export const SHEET_ROOMING_LIST = 'ROOMING LIST'
export const SHEET_SUMMARY = 'SUMMARY'

export const COMPANIES_HEADERS = ['Company Code', 'Company Name', 'Contact Person', 'Contact Mobile', 'Contact Email', 'Deadline', 'Portal Status', 'Notes']
export const ALLOCATIONS_HEADERS = ['Company Code', 'Hotel', 'Room Type', 'Room Count', 'Capacity Per Room', 'Check-in', 'Check-out', 'Notes']
export const GUESTS_HEADERS = [
  'Company Code',
  'Hotel',
  'Room Type',
  'Room Slot',
  'Guest Slot',
  'Guest Name',
  'Mobile',
  'Email',
  'Check-in',
  'Check-out',
  'Notes',
  'Guest ID (internal)',
]

const HEADER_FILL: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3866' } }
const HEADER_FONT: Partial<ExcelJS.Font> = { color: { argb: 'FFFFFFFF' }, bold: true }

export function styleHeaderRow(row: ExcelJS.Row) {
  row.eachCell((cell) => {
    cell.fill = HEADER_FILL
    cell.font = HEADER_FONT
    cell.alignment = { vertical: 'middle' }
  })
  row.height = 20
}

export function autoSizeColumns(sheet: ExcelJS.Worksheet, minWidth = 10, maxWidth = 40) {
  sheet.columns.forEach((col) => {
    let max = minWidth
    col.eachCell?.({ includeEmpty: false }, (cell) => {
      const len = (cell.value ? String(cell.value) : '').length
      if (len > max) max = Math.min(maxWidth, len + 2)
    })
    col.width = max
  })
}

export async function workbookToBlob(wb: ExcelJS.Workbook): Promise<Blob> {
  const buffer = await wb.xlsx.writeBuffer()
  return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export interface ParsedSheet {
  name: string
  headers: string[]
  rows: Record<string, string>[]
}

/** Reads every worksheet of an uploaded file into header-keyed row objects. Cell values are stringified/trimmed; callers coerce to number/date as needed per column. */
export async function parseWorkbookFile(file: File): Promise<ParsedSheet[]> {
  const wb = new ExcelJS.Workbook()
  const buf = await file.arrayBuffer()
  await wb.xlsx.load(buf)

  const sheets: ParsedSheet[] = []
  wb.eachSheet((sheet) => {
    const headerRow = sheet.getRow(1)
    const headers: string[] = []
    headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
      headers[colNumber - 1] = String(cell.value ?? '').trim()
    })
    const rows: Record<string, string>[] = []
    sheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return
      const obj: Record<string, string> = {}
      let hasValue = false
      headers.forEach((h, idx) => {
        if (!h) return
        const cell = row.getCell(idx + 1)
        let value = cell.value
        if (value && typeof value === 'object' && 'result' in (value as any)) value = (value as any).result
        if (value instanceof Date) {
          obj[h] = value.toISOString().slice(0, 10)
        } else {
          obj[h] = value == null ? '' : String(value).trim()
        }
        if (obj[h]) hasValue = true
      })
      if (hasValue) rows.push(obj)
    })
    sheets.push({ name: sheet.name, headers, rows })
  })
  return sheets
}

export function findSheet(sheets: ParsedSheet[], name: string): ParsedSheet | undefined {
  return sheets.find((s) => s.name.trim().toLowerCase() === name.toLowerCase())
}
